import { G } from '@mobily/ts-belt';
import { InputAdornment, MenuItem, TextField } from '@mui/material';
import { format } from 'date-fns-tz';
import { useMemo } from 'react';
import { InputProps, useInput, useTranslate } from 'react-admin';

import { TranslationKeys } from '@sh4res/bo-common/i18n/types';

export const DateInputOnlyYear = ({
  day,
  month,
  label,
  'data-testid': dataTestId,
  ...props
}: Omit<InputProps, 'label'> & {
  month: number;
  day: number;
  label: TranslationKeys;
  'data-testid'?: string;
}) => {
  const {
    field,
    fieldState: { error },
    isRequired,
  } = useInput({ ...props });

  const translate = useTranslate();

  const value = field.value ? new Date(field.value).getFullYear() : '';
  const years = useMemo(
    () =>
      Array.from(
        { length: new Date().getFullYear() - 1950 + 1 },
        (_, idx) => new Date().getFullYear() - idx,
      ),
    [],
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const year = parseInt(event.target.value, 10);
    if (!isNaN(year) && !!year) {
      const newDate = new Date(year, month - 1, day);
      field.onChange(format(newDate, 'yyyy-MM-dd'));
      field.onBlur();
    }
    props.onChange?.();
  };

  return (
    <TextField
      select
      data-testid={dataTestId}
      InputProps={{
        startAdornment: (
          <InputAdornment
            position="start"
            sx={{
              mr: 0,
            }}>{`${G.isNullable(day) ? '__' : String(day).padStart(2, '0')}  /  ${G.isNullable(month) ? '__' : String(month).padStart(2, '0')}  /`}</InputAdornment>
        ),
      }}
      value={value}
      required={isRequired}
      error={!!error}
      helperText={error?.message
        ?.replace('@@react-admin@@', '')
        .replace(/^"/, '')
        .replace(/"$/, '')}
      fullWidth
      label={translate(label)}
      size="small"
      SelectProps={{ displayEmpty: true }}
      {...props}
      onChange={handleChange}>
      <MenuItem value="" disabled data-testid={`${dataTestId}_EmptyMenuItem`}>
        {translate('resources.bo_common.year')}
      </MenuItem>
      {years.map((year) => (
        <MenuItem key={year} value={year} data-testid={`${dataTestId}_YearMenuItem`}>
          {year}
        </MenuItem>
      ))}
    </TextField>
  );
};
